import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandShortcut,
} from '@neo/primitives/command'
import { Dialog, DialogContent, DialogTitle } from '@neo/primitives/dialog'
import type { UseCommandPaletteReturn } from 'cmdk-engine/react'
import { useCommandRegister } from 'cmdk-engine/react'
import { X } from 'lucide-react'
import { useEffect, useMemo } from 'react'
import { useUserPlan } from 'v2/shared/hooks/use-plan'
import { useRoutes } from 'v2/shared/hooks/use-routes'
import { getShortcutModifierLabel } from 'v2/shared/utils/platform'
import { routesToCommands } from './routes-to-commands'

interface GlobalCommandPaletteProps {
  // A single shared `useCommandPalette()` instance, owned by MainLayout — cmdk-engine 0.3.0
  // keeps isOpen/search/results as local state per hook call, so every consumer that needs to
  // open/read the SAME palette (this component, plus the sidebar search bar) must be handed
  // the one instance rather than each calling the hook itself.
  commandPalette: UseCommandPaletteReturn
}

export const GlobalCommandPalette = ({ commandPalette }: GlobalCommandPaletteProps) => {
  const routes = useRoutes()
  // Route-derived commands carry `locked` from useRoutes, which reads `isEnterpriseLocked` —
  // false until the profile resolves. routesToCommands withholds locked destinations, but on
  // first paint nothing is locked yet, so a plan-gated route (Verify, WhatsApp) would be
  // registered and selectable for that window. Register nothing until the plan is KNOWN, the
  // same fail-closed rule getTabbedPageCommands uses for the explicit tab commands.
  //
  // Withholding briefly costs nothing here: the palette only opens on an explicit cmd+K and
  // the profile is fetched at app boot, so the window closes long before it can be opened.
  // The fix can't live in useRoutes — the sidebar shares it and must keep failing OPEN, or
  // Enterprise users would see locks flash on every load.
  const { isPlanLoaded } = useUserPlan()
  const commands = useMemo(
    () => (isPlanLoaded ? routesToCommands(routes) : []),
    [routes, isPlanLoaded],
  )

  useCommandRegister(commands, [commands])

  const { isOpen, open, close, toggle, search, setSearch, groupedResults, select } = commandPalette

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        toggle()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [toggle])

  return (
    <Dialog open={isOpen} onOpenChange={(next) => (next ? open() : close())}>
      <DialogContent className="overflow-hidden p-0" showCloseButton={false}>
        {/* Visually hidden — DialogContent otherwise has no accessible name for screen readers. */}
        <DialogTitle className="sr-only">Command Palette</DialogTitle>
        <Command
          shouldFilter={false}
          className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:text-xs-500 [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]>svg]:h-5 [&_[cmdk-input-wrapper]>svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-4 [&_[cmdk-item]_svg]:w-4 [&_[cmdk-item]_svg]:text-secondary"
        >
          <CommandInput
            placeholder="Search"
            value={search}
            onValueChange={setSearch}
            data-testid="command-palette-search-input"
            suffix={
              <div className="ml-2 flex shrink-0 items-center gap-1.5">
                <span className="rounded-full border bg-muted px-2 py-0.5 text-xxs-600 text-tertiary">
                  {getShortcutModifierLabel()}K
                </span>
                <button
                  type="button"
                  onClick={close}
                  className="flex h-5 w-5 items-center justify-center rounded-full text-tertiary transition-colors hover:bg-accent hover:text-foreground"
                  data-testid="command-palette-close-btn"
                >
                  <X className="h-3.5 w-3.5" />
                  <span className="sr-only">Close</span>
                </button>
              </div>
            }
          />
          <CommandList>
            {groupedResults.length === 0 ? (
              <CommandEmpty>
                <span className="flex flex-col items-center gap-1">
                  <span>No results found.</span>
                  <span className="text-tertiary text-xs-400">
                    Tip: active conversations may not appear here — check your inbox list.
                  </span>
                </span>
              </CommandEmpty>
            ) : (
              groupedResults.map(({ group, items }) => (
                <CommandGroup key={group.id} heading={group.label}>
                  {items.map(({ item }) => (
                    <CommandItem
                      key={item.id}
                      value={item.id}
                      data-testid={`command-palette-item-${item.id}`}
                      onSelect={() => {
                        // select() records frecency, runs the Provider's onSelect (which
                        // navigates for href-based items), and closes the palette. It does
                        // NOT run `item.action` once a Provider onSelect is configured (see
                        // MainLayout), so action-only commands (dark mode, resolve, etc.)
                        // are invoked explicitly here.
                        select(item)
                        item.action?.(item)
                      }}
                    >
                      {/* Icons intentionally not rendered — the palette is a uniform
                          text list (only some routes/commands carry an icon, so showing
                          them looked inconsistent). */}
                      <span>{item.label}</span>
                      {item.shortcut && <CommandShortcut>{item.shortcut.join('')}</CommandShortcut>}
                    </CommandItem>
                  ))}
                </CommandGroup>
              ))
            )}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  )
}
